import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { getMessaging, onMessage, getToken } from "firebase/messaging";
import { cmFirebaseToken, orders } from '@/global/state/GlobalState';
import { appearNotification, notifications, addNotifications } from '@/global/state/NotificationState';
import { restaurant } from '@/global/state/GlobalState';
import { customer } from '@/global/state/AuthState';
import axiosClient from '@/api/axios';
import { OrderModel } from '@/models/OrderModel';
import router from '@/router';
import { watch } from 'vue';

const firebaseConfig = {
    apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
    authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.VUE_APP_FIREBASE_APP_ID,
    measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
};

const firebaseApp = firebase.initializeApp(firebaseConfig);
const messaging = getMessaging(firebaseApp)

// Se o cliente estiver visualizando este pedido, refaz a busca para atualizar os dados em tempo real.
// Extraído para ser reutilizável tanto pelo onMessage (app em primeiro plano) quanto pelas mensagens
// retransmitidas pelo service worker (app em segundo plano), já que o onMessage só dispara com a aba em foco.
function refetchOrderIfViewing(orderId: string | number | undefined) {
    if (!orderId) return

    const currentRoute = router.currentRoute.value
    if (currentRoute.name !== 'orders.show') return

    const routeId = String(currentRoute.params.id)
    const existingOrder = orders.value.find((o: OrderModel) => String(o.id) === String(orderId))
    const isViewingThisOrder = routeId === String(orderId) || (!!existingOrder && routeId === existingOrder.uuid)

    if (!isViewingThisOrder) return

    const identifier = existingOrder?.uuid || routeId
    axiosClient.get(`/customer/orders/${identifier}`).then((res) => {
        const updatedOrder = res.data
        const index = orders.value.findIndex((o: OrderModel) => o.id === updatedOrder.id || (updatedOrder.uuid && o.uuid === updatedOrder.uuid))
        if (index !== -1) {
            orders.value.splice(index, 1, updatedOrder)
        } else {
            orders.value.push(updatedOrder)
        }
    }).catch((err) => {
        console.error('Failed to refetch order after status update:', err)
    })
}

if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        if (event.data?.type === 'ORDER_STATUS_UPDATED') {
            refetchOrderIfViewing(event.data.orderId)
        }
    })
}

onMessage(messaging, (payload: any) => {
    const data = payload.data;
    if (data.type === 'order_status' || data.type === 'kitchen_status' || data.type === 'counter_status') {
        const statusTranslations: any = {
            'pending': { label: 'pendente', color: '#918373' },
            'confirmed': { label: 'confirmado', color: '#FF9B3D' },
            'preparing': { label: 'preparando', color: '#FF9B3D' },
            'ready': { label: 'pronto', color: '#59A6F8' },
            'dispatched': { label: 'saiu para entrega', color: '#59A6F8' },
            'completed': { label: 'entregue', color: '#00c9a7' },
            'canceled': { label: 'cancelado', color: '#F83535' },
            'failed': { label: 'falhou', color: '#F83535' },
            // Mapeamentos para compatibilidade com versões anteriores se necessário
            'prepared': { label: 'preparado', color: '#59A6F8' },
            'delivered': { label: 'entregue', color: '#00c9a7' },
            'cancelled': { label: 'cancelado', color: '#F83535' },
            'finished': { label: 'finalizado' }
        }

        const type = data.type;
        const orderId = data.orderId || data.order_id;
        const oldStatus = data.oldStatus || data.old_status;
        const newStatus = data.newStatus || data.new_status;

        const statusTypeLabel = type === 'order_status' ? '' : (type === 'kitchen_status' ? ' de cozinha' : ' de balcão')

        const oldLabel = statusTranslations[oldStatus]?.label || oldStatus;
        const newLabel = statusTranslations[newStatus]?.label || newStatus;

        const notificationData = {
            orderId: orderId,
            title: data.title || 'Seu pedido foi atualizado',
            message: data.body || `O status${statusTypeLabel} do seu pedido mudou de ${oldLabel} para ${newLabel}`,
            date: new Date(),
            restaurantId: restaurant.value.id,
            type: type,
            newStatus: newStatus,
            isRead: false
        }

        notifications.value.push(notificationData)
        addNotifications([notificationData])
        appearNotification(notificationData.title, notificationData.message)

        refetchOrderIfViewing(orderId)
    } else if (data.type === 'answer_customer_request') {
        const notificationData = {
            title: data.title,
            message: data.body,
            date: new Date(),
            restaurantId: restaurant.value.id,
            type: data.type,
            answer: data.answer,
            customerRequestType: data.customerRequestType || data.customer_request_type,
            isRead: false
        }

        notifications.value.push(notificationData)
        addNotifications([notificationData])
        appearNotification(data.title, data.body, data.answer === 'refuse')
    }
});

// Identificador estável deste aparelho, para a API atualizar o token em vez de duplicar
function getDeviceFingerprint(): string {
    const stored = localStorage.getItem('deviceFingerprint')
    if (stored) return stored

    const cryptoApi: any = typeof crypto !== 'undefined' ? crypto : null
    const fingerprint: string = cryptoApi?.randomUUID
        ? 'web-customer-' + cryptoApi.randomUUID()
        : `web-customer-${Date.now()}-${Math.random().toString(36).slice(2)}`
    localStorage.setItem('deviceFingerprint', fingerprint)
    return fingerprint
}

// O cliente só tem uuid depois de identificado (GET /customer/cpf), o que pode acontecer
// depois do carregamento da página. Por isso o registro é refeito quando o uuid muda.
let registeredFor: string | null = null

function registerPushToken() {
    const token = cmFirebaseToken.value
    const customerUuid = customer.value?.uuid
    if (!token || !customerUuid || registeredFor === `${customerUuid}:${token}`) return

    registeredFor = `${customerUuid}:${token}`
    axiosClient.post('/push-notification-tokens', {
        token,
        customerUuid,
        fingerprint: getDeviceFingerprint()
    }).then(() => {
        console.log('Push notification token updated successfully')
    }).catch(err => {
        registeredFor = null
        console.error('Failed to update push notification token:', err)
    })
}

watch(() => customer.value?.uuid, registerPushToken)

async function initPushToken() {
    if (!('Notification' in window)) return

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
        console.log('Permissão para notificações negada.')
        return
    }

    const currentToken = await getToken(messaging, { vapidKey: 'BMO5D3aM00o8csdPvxIFkMLZa6ad0aOswN3cl-uQVl-NbgZB8dqsWWeKSkGbN4hA9N-NgsrcD6yS1CMNPhjp04g' })
    if (!currentToken) return

    localStorage.setItem('cmFirebaseToken', currentToken)
    cmFirebaseToken.value = currentToken
    registerPushToken()
}

initPushToken().catch((err: any) => {
    console.log('An error occurred while retrieving token. ', err)
})

export default messaging